import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Mail, Menu, Phone, X } from "lucide-react";
import { useLang } from "../LanguageContext";
import { scrollToId } from "../scroll";

const SECTION_IDS = ["chi-sono", "competenze", "perche", "testimonianze", "libri", "contatti"];

const LangSwitch = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-switch"
      className={`flex items-center rounded-full border p-1 ${dark ? "border-linen/25" : "border-ink/15"}`}
    >
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`relative rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "text-linen" : dark ? "text-linen/60 hover:text-linen" : "text-ink/60 hover:text-ink"
          }`}
        >
          {lang === l && (
            <motion.span
              layoutId={dark ? "lang-pill-dark" : "lang-pill"}
              className="absolute inset-0 rounded-full bg-terra"
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
            />
          )}
          <span className="relative z-10">{l}</span>
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  const labels = [t.nav.about, t.nav.disciplines, t.nav.why, t.nav.testimonials, t.nav.books, t.nav.contact];

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-4 z-[70] flex justify-center px-4">
        <motion.header
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className={`pointer-events-auto flex w-full max-w-6xl items-center justify-between rounded-full px-5 py-3 transition-colors duration-500 sm:px-7 ${
            scrolled ? "border border-ink/10 bg-linen/80 shadow-lg shadow-forest/5 backdrop-blur-xl" : "border border-linen/15 bg-forest/25 backdrop-blur-xl"
          }`}
        >
        <button
          data-testid="nav-brand-logo"
          onClick={() => scrollToId("hero")}
          className="flex items-center gap-2.5 text-left"
        >
          <Leaf className={`h-5 w-5 ${scrolled ? "text-forest" : "text-linen"}`} strokeWidth={1.5} />
          <span className={`font-serif text-lg leading-none tracking-tight ${scrolled ? "text-forest" : "text-linen"}`}>
            Eros Zannoni
          </span>
        </button>
        <div className="flex items-center gap-3 sm:gap-5">
          <LangSwitch dark={!scrolled} />
          <button
            data-testid="nav-drawer-toggle"
            onClick={() => setOpen(true)}
            aria-label={t.nav.menuLabel}
            className={`flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
              scrolled ? "bg-forest text-linen hover:bg-olive" : "bg-linen/15 text-linen hover:bg-linen/25"
            }`}
          >
            <Menu className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">{t.nav.menuLabel}</span>
          </button>
        </div>
        </motion.header>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[80] bg-forest/60 backdrop-blur-sm"
            />
            <motion.aside
              key="drawer"
              data-testid="offcanvas-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 32 }}
              className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col overflow-y-auto bg-forest p-8 text-linen sm:p-12"
            >
              <Leaf className="pointer-events-none absolute -bottom-16 -right-16 h-72 w-72 text-olive/40" strokeWidth={0.5} />
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{t.nav.drawerKicker}</p>
                <button
                  data-testid="drawer-close-button"
                  onClick={() => setOpen(false)}
                  aria-label={t.nav.closeLabel}
                  className="rounded-full border border-linen/25 p-2.5 transition-colors duration-300 hover:bg-linen/10"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <nav className="relative z-10 mt-14 flex flex-col gap-1">
                {labels.map((label, i) => (
                  <motion.button
                    key={SECTION_IDS[i]}
                    data-testid={`drawer-link-${SECTION_IDS[i]}`}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => scrollToId(SECTION_IDS[i]), 350);
                    }}
                    className="group flex items-baseline gap-4 border-b border-linen/10 py-4 text-left"
                  >
                    <span className="font-mono text-[11px] text-gold/80">0{i + 1}</span>
                    <span className="font-serif text-2xl font-light tracking-tight transition-colors duration-300 group-hover:text-gold sm:text-3xl">
                      {label}
                    </span>
                  </motion.button>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="relative z-10 mt-auto space-y-4 pt-14"
              >
                <div className="flex items-center gap-4 pb-2">
                  <img
                    src="/images/eros-portrait.jpg"
                    alt="Eros Zannoni"
                    data-testid="drawer-portrait"
                    className="h-16 w-16 rounded-full border border-gold/40 object-cover"
                  />
                  <p className="font-serif text-lg font-light italic leading-snug text-linen/80">Eros Zannoni</p>
                </div>
                <LangSwitch dark />
                <a href="tel:+393317642541" className="flex items-center gap-3 text-sm text-linen/80 transition-colors duration-300 hover:text-gold">
                  <Phone className="h-4 w-4" strokeWidth={1.5} /> +39 331 764 2541
                </a>
                <a href="mailto:eros.zannoni@gmail.com" className="flex items-center gap-3 text-sm text-linen/80 transition-colors duration-300 hover:text-gold">
                  <Mail className="h-4 w-4" strokeWidth={1.5} /> eros.zannoni@gmail.com
                </a>
                <p className="pt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-linen/40">{t.nav.drawerNote}</p>
              </motion.div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
